import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  InvitationForm,
  InvitationFormData,
} from "@/app/components/invitation/InvitationForm";
import {
  RenterMetaForm,
  RenterMetaFormData,
} from "@/app/components/invitation/RenterMetaForm";
import { InvitationReview } from "@/app/components/invitation/InvitationReview";
import { Stepper } from "@/app/components/initiate/Stepper";
import { ModalError } from "@/app/components/modals/modalBuilder";
import { ConfirmDeleteModal } from "@/app/components/modals/service/confirmDeleteModal";
import { useDisclosure } from "@heroui/react";
import useErrorHandling from "@/hooks/useErrorHandling";
import { api } from "@/api";
import { normalizeDate } from "@/utils";
import { InvitationUpdate } from "@/api/invitations";
import { useAppSelector } from "@/hooks/useAppSelector";
import {
  OfficesSelectionForm,
  OfficesSelectionFormData,
} from "@/app/components/invitation/OfficesSelectionForm";

export function EditNewInvitationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const handleError = useErrorHandling();
  const businessProfileId = useAppSelector(
    (state) => state.businessProfile.businessProfile.id,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [defaultInvitationValues, setDefaultInvitationValues] =
    useState<Partial<InvitationFormData>>();
  const [defaultOfficesValues, setDefaultOfficesValues] =
    useState<OfficesSelectionFormData | null>(null);
  const [defaultRenterMetaValues, setDefaultRenterMetaValues] =
    useState<Partial<RenterMetaFormData>>();
  const [invitationData, setInvitationData] =
    useState<InvitationFormData | null>(null);
  const [officesData, setOfficesData] =
    useState<OfficesSelectionFormData | null>(null);
  const [renterMetaData, setRenterMetaData] =
    useState<RenterMetaFormData | null>(null);
  const [modalErrors, setModalErrors] = useState<ModalError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const deleteConfirmDisclosure = useDisclosure();

  const steps = [
    {
      name: "Данные приглашения",
      description: "Заполните основную информацию",
      longDescription: "Укажите параметры договора",
    },
    {
      name: "Офисы",
      description: "Выберите офисы и рабочие места",
      longDescription: "Укажите офисы и рабочие места для договора",
    },
    {
      name: "Данные арендатора",
      description: "Заполните информацию об арендаторе",
      longDescription:
        "Введите реквизиты и контактные данные нового арендатора",
    },
    {
      name: "Проверка данных",
      description: "Проверьте введенные данные",
      longDescription: "Убедитесь, что все данные корректны перед отправкой",
    },
  ];

  useEffect(() => {
    if (!id) return;

    const loadInvitation = async () => {
      try {
        const invitationResponse = await api.invitations.get(Number(id));
        const invitation = invitationResponse.data;

        if (invitation.type !== "new_renter") {
          toast.error("Неправильный тип приглашения");
          navigate("/dashboard/invitations");
          return;
        }

        // Получаем данные договора для получения номера договора
        const agreement = await api.agreements.get(invitation.agreement_id);

        setDefaultInvitationValues({
          type: "new_renter",
          number: agreement.number,
          startDate: normalizeDate(invitation.start_date),
          endDate: normalizeDate(invitation.end_date),
          meetingHours: agreement.meetingHours,
          requisite: agreement.requisiteId || undefined,
        });

        // Загружаем офисы и рабочие места из договора
        const combinedOffices: Array<{
          office_id: number | null;
          workplace_id: number | null;
          price: number;
        }> = [
          ...agreement.offices.map((o) => ({
            office_id: o.officeId,
            price: o.price,
            workplace_id: null,
          })),
        ];

        setDefaultOfficesValues({
          offices: combinedOffices,
        });

        // Получаем meta данные нового арендатора
        if (invitation.meta_id) {
          try {
            const metaResponse = await api.invitations.getMeta(
              invitation.meta_id,
            );
            const metaData = metaResponse.data;

            // Проверяем тип meta данных (по наличию renter_name)
            if ("renter_name" in metaData) {
              setDefaultRenterMetaValues({
                renter_name: metaData.renter_name,
                address: metaData.address,
                ogrn: metaData.ogrn,
                inn: metaData.inn,
                kpp: metaData.kpp,
                bik: metaData.bik,
                bank_account: metaData.bank_account,
                correspondent_account: metaData.correspondent_account,
                bank_name: metaData.bank_name,
                phone_number: metaData.phone_number,
                email: metaData.email,
              });
            } else {
              setDefaultRenterMetaValues({});
            }
          } catch (metaErr: any) {
            // Если не удалось загрузить meta данные, продолжаем без них
            console.error("Failed to load meta data:", metaErr);
            setDefaultRenterMetaValues({});
          }
        } else {
          setDefaultRenterMetaValues({});
        }

        setIsLoading(false);
      } catch (err: any) {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
        navigate("/dashboard/invitations");
      }
    };

    loadInvitation();
  }, [id, navigate, handleError]);

  const handleInvitationSubmit = useCallback((data: InvitationFormData) => {
    setInvitationData(data);
    setDefaultInvitationValues(data);
    setModalErrors([]);
    setActiveStep(1); // Переход к шагу выбора офисов
  }, []);

  const handleOfficesSubmit = useCallback((data: OfficesSelectionFormData) => {
    setOfficesData(data);
    setDefaultOfficesValues(data);
    setModalErrors([]);
    setActiveStep(2); // Переход к шагу данных арендатора
  }, []);

  const handleRenterMetaSubmit = useCallback((data: RenterMetaFormData) => {
    setRenterMetaData(data);
    setDefaultRenterMetaValues(data);
    setModalErrors([]);
    setActiveStep(3); // Переход к проверке данных
  }, []);

  const handleReviewSubmit = useCallback(async () => {
    if (!invitationData || !renterMetaData || !id || !officesData) return;

    setIsSubmitting(true);

    try {
      // Разделяем офисы и рабочие места
      const offices = officesData.offices
        .filter((item) => item.office_id !== null && !item.workplace_id)
        .map((item) => ({
          office_id: item.office_id!,
          price: item.price,
        }));

      const workplaces = officesData.offices
        .filter((item) => item.workplace_id !== null)
        .map((item) => ({
          workplace_id: item.workplace_id!,
          price: item.price,
        }));

      const payload: InvitationUpdate = {
        business_profile_id: businessProfileId,
        start_date: invitationData.startDate,
        end_date: invitationData.endDate,
        meetingHours: invitationData.meetingHours,
        offices: offices,
        workplaces: workplaces,
        requisite: invitationData.requisite,
        type: "new_renter",
        number: invitationData.number,
        meta: {
          renter_name: renterMetaData.renter_name,
          address: renterMetaData.address,
          ogrn: renterMetaData.ogrn,
          inn: renterMetaData.inn,
          kpp: renterMetaData.kpp,
          bik: renterMetaData.bik,
          bank_account: renterMetaData.bank_account,
          correspondent_account: renterMetaData.correspondent_account,
          bank_name: renterMetaData.bank_name,
          phone_number: renterMetaData.phone_number,
          email: renterMetaData.email,
        },
      };

      await api.invitations.update(Number(id), payload);

      toast.success("Приглашение успешно обновлено");
      setIsSubmitting(false);
      navigate("/dashboard/invitations");
    } catch (err: any) {
      const { errorMessage, parsedErrors } = handleError(err);
      setModalErrors(parsedErrors || []);
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  }, [
    invitationData,
    officesData,
    renterMetaData,
    id,
    businessProfileId,
    navigate,
    handleError,
  ]);

  const handleEdit = useCallback(() => {
    if (renterMetaData) {
      setActiveStep(2); // Редактирование данных арендатора
    } else if (officesData || defaultOfficesValues) {
      setActiveStep(1); // Редактирование офисов
    } else {
      setActiveStep(0); // Редактирование данных приглашения
    }
  }, [renterMetaData, officesData, defaultOfficesValues]);

  const changeStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  const handleDelete = useCallback(() => {
    deleteConfirmDisclosure.onOpen();
  }, [deleteConfirmDisclosure]);

  const confirmDelete = useCallback(async () => {
    if (!id) return;

    try {
      await api.invitations.delete(Number(id));
      toast.success("Приглашение успешно удалено");
      navigate("/dashboard/invitations");
    } catch (err: any) {
      const { errorMessage } = handleError(err);
      toast.error(errorMessage);
    }
  }, [id, navigate, handleError]);

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  return (
    <>
      <ConfirmDeleteModal
        isOpen={deleteConfirmDisclosure.isOpen}
        onOpenChange={deleteConfirmDisclosure.onOpenChange}
        callback={confirmDelete}
      />

      <h1 className="text-xl font-bold mb-4 mt-2">
        Редактирование приглашения
      </h1>

      <div className="flex flex-col lg:flex-row gap-4 w-full">
        <div className="lg:w-1/3">
          <Stepper
            steps={steps}
            activeStep={activeStep}
            changeStep={changeStep}
            colorScheme="primary"
            completedSteps={[0, 1, 2]}
          />
        </div>

        <div className="lg:w-2/3">
          <div className="w-full h-full p-8 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl bg-background">
            <div className="flex flex-col gap-4">
              {!(
                "hideTitle" in steps[activeStep] && steps[activeStep].hideTitle
              ) && (
                <div className="flex flex-col gap-2">
                  <span className="text-lg font-semibold">
                    {steps[activeStep].name}
                  </span>
                  <span className="text-sm text-gray-500">
                    {steps[activeStep].longDescription}
                  </span>
                </div>
              )}

              {activeStep === 0 && (
                <InvitationForm
                  type="new_renter"
                  defaultValues={invitationData || defaultInvitationValues}
                  onSubmit={handleInvitationSubmit}
                  isEdit={true}
                  modalErrors={modalErrors}
                  onDelete={handleDelete}
                />
              )}

              {activeStep === 1 && (
                <OfficesSelectionForm
                  defaultValues={
                    officesData || defaultOfficesValues || undefined
                  }
                  onSubmit={handleOfficesSubmit}
                  modalErrors={modalErrors}
                />
              )}

              {activeStep === 2 && (
                <RenterMetaForm
                  defaultValues={renterMetaData || defaultRenterMetaValues}
                  onSubmit={handleRenterMetaSubmit}
                  isEdit={true}
                  modalErrors={modalErrors}
                  onDelete={handleDelete}
                />
              )}

              {activeStep === 3 && (
                <InvitationReview
                  invitationData={
                    invitationData ||
                    (defaultInvitationValues as InvitationFormData)
                  }
                  renterMetaData={
                    renterMetaData ||
                    (defaultRenterMetaValues as RenterMetaFormData)
                  }
                  officesData={officesData || defaultOfficesValues || undefined}
                  onEdit={handleEdit}
                  onSubmit={handleReviewSubmit}
                  onDelete={handleDelete}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
